import axios from "axios";
import { store } from "../store";
import { handleErrorMessage } from "./classes";
import { errorMessageFromResponse } from "../constants/commonFunctions";
import {
  GET_VAPID_PUBLIC_KEY_API,
  GET_NOTIFICATIONS_API,
  MARK_ALL_NOTIFICATIONS_READ_API,
  MARK_NOTIFICATION_READ_API,
  SUBSCRIBE_WEB_PUSH_API,
} from "../constants/apis";
import {
  GET_VAPID_PUBLIC_KEY,
  GET_NOTIFICATIONS,
  MARK_ALL_NOTIFICATIONS_READ,
  MARK_NOTIFICATION_READ,
  SUBSCRIBE_WEB_PUSH,
  NOTIFICATIONS_LOADING,
  NOTIFICATIONS_ERROR,
} from "../constants/actionTypes";

/**
 * GET /api/v1/notificationsexpress/vapid-public-key/
 * Fetches the VAPID public key needed for web push subscription
 */
export const getVapidPublicKey = () => {
  return async (dispatch) => {
    dispatch({ type: NOTIFICATIONS_LOADING });
    try {
      const response = await axios.get(GET_VAPID_PUBLIC_KEY_API);
      dispatch({ type: GET_VAPID_PUBLIC_KEY, payload: response.data });
      return response.data;
    } catch (error) {
      dispatch(handleErrorMessage(errorMessageFromResponse(error)));
      dispatch({ type: NOTIFICATIONS_ERROR, payload: errorMessageFromResponse(error) });
      return null;
    }
  };
};

/**
 * GET /api/v1/notificationsexpress/?type=<type>
 * Fetches all notifications, optionally filtered by type
 * @param {string} type - Optional notification type filter (BOOKING, RESCHEDULE, CANCELLATION, PAYMENT, REVIEW)
 */
export const getNotifications = (type) => {
  const token = store.getState()?.profile?.user?.token;

  return async (dispatch) => {
    dispatch({ type: NOTIFICATIONS_LOADING });
    try {
      const url = type
        ? `${GET_NOTIFICATIONS_API}?type=${encodeURIComponent(type)}`
        : GET_NOTIFICATIONS_API;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      dispatch({ type: GET_NOTIFICATIONS, payload: response.data.data });
      return response.data;
    } catch (error) {
      dispatch(handleErrorMessage(errorMessageFromResponse(error)));
      dispatch({ type: NOTIFICATIONS_ERROR, payload: errorMessageFromResponse(error) });
      return null;
    }
  };
};

/**
 * PATCH /api/v1/notificationsexpress/mark-all-read/
 * Marks all notifications as read
 */
export const markAllNotificationsRead = () => {
  const token = store.getState()?.profile?.user?.token;

  return async (dispatch) => {
    try {
      const response = await axios.patch(
        MARK_ALL_NOTIFICATIONS_READ_API,
        {},
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      dispatch({ type: MARK_ALL_NOTIFICATIONS_READ });
      return response.data;
    } catch (error) {
      dispatch(handleErrorMessage(errorMessageFromResponse(error)));
      return null;
    }
  };
};

/**
 * PATCH /api/v1/notificationsexpress/<id>/mark-read/
 * Marks a single notification as read
 * @param {number} notificationId - The ID of the notification to mark as read
 */
export const markNotificationRead = (notificationId) => {
  const token = store.getState()?.profile?.user?.token;

  return async (dispatch) => {
    try {
      const response = await axios.patch(
        `${MARK_NOTIFICATION_READ_API}${notificationId}/mark-read/`,
        {},
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      dispatch({ type: MARK_NOTIFICATION_READ, payload: notificationId });
      return response.data;
    } catch (error) {
      dispatch(handleErrorMessage(errorMessageFromResponse(error)));
      return null;
    }
  };
};

/**
 * POST /api/v1/notifications/subscribe-webpush/
 * Subscribes the current browser to web push notifications
 * @param {Object} subscriptionData - { profile, endpoint, p256dh, auth, user_agent }
 */
export const subscribeWebPush = (subscriptionData) => {
  const token = store.getState()?.profile?.user?.token;

  return async (dispatch) => {
    try {
      const response = await axios.post(
        SUBSCRIBE_WEB_PUSH_API,
        subscriptionData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        }
      );
      dispatch({ type: SUBSCRIBE_WEB_PUSH, payload: response.data });
      return response.data;
    } catch (error) {
      dispatch(handleErrorMessage(errorMessageFromResponse(error)));
      dispatch({ type: NOTIFICATIONS_ERROR, payload: errorMessageFromResponse(error) });
      return null;
    }
  };
};
